import { layoutTokens, typographyTokens, surfaceTokens, ACTIVE_SURFACE, ACTIVE_SURFACE_MUTED } from "@/shared/theme";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Box, ButtonBase, IconButton, Popover, Typography } from "@mui/material";
import { useState } from "react";
import type { DevelopmentArea } from "../patterns.api";
import { DevelopmentGlyph } from "./DevelopmentGlyph";
import { MicroLabel } from "./PatternTypography";

interface DevelopmentOverviewProps {
  readonly areas: readonly DevelopmentArea[];
  readonly analyzedGames: number;
  readonly selected: DevelopmentArea["key"] | null;
  readonly onSelect: (key: DevelopmentArea["key"] | null) => void;
}

const maturityLabel = {
  observation: "Observation",
  early: "Early signal",
  developing: "Developing evidence",
  established: "Established evidence",
} as const;

const areaHelp: Record<string, { meaning: string; measures: string; improvement: string }> = {
  forcing_calculation: {
    meaning: "Checks, captures, and direct threats that limit the opponent's choices.",
    measures: "Verified moments where one more forcing-move scan would have found a stronger continuation.",
    improvement: "These misses appear in fewer games and cost fewer pawns when they do appear.",
  },
  tactical_geometry: {
    meaning: "How pieces line up for forks, pins, skewers, and discovered attacks.",
    measures: "Repeated positions where board geometry created a tactical chance or exposed one of your pieces.",
    improvement: "You spot the relationship earlier, before the tactic becomes an expensive mistake.",
  },
  piece_safety: {
    meaning: "Whether every piece is protected, coordinated, and still doing its defensive job.",
    measures: "Loose pieces, overloaded defenders, and pieces deflected away from something important.",
    improvement: "Fewer pieces become tactical targets, and your defenders keep supporting each other.",
  },
  king_safety: {
    meaning: "Your king's shelter, escape squares, and vulnerability to forcing attacks.",
    measures: "Verified mating nets and back-rank threats where safe squares disappeared.",
    improvement: "You preserve escape squares and address dangerous threats before they become forcing.",
  },
};

export const DevelopmentOverview = ({ areas, analyzedGames, selected, onSelect }: DevelopmentOverviewProps) => {
  const [info, setInfo] = useState<{ anchor: HTMLElement; area: DevelopmentArea } | null>(null);
  if (areas.length === 0)
    return (
      <Box sx={{ border: layoutTokens.dividerBorder, borderColor: "divider", mt: 2, p: 2 }}>
        <MicroLabel>Development overview</MicroLabel>
        <Typography
          sx={{
            color: "text.secondary",
            fontSize: "caption.fontSize",
            lineHeight: typographyTokens.lineHeight.body,
            mt: 0.75,
          }}
        >
          No supported development area yet. Analyze any game to add evidence; nothing is locked.
        </Typography>
      </Box>
    );

  return (
    <Box component="section" aria-label="Development overview" sx={{ mt: 2 }}>
      <Box sx={{ alignItems: "end", display: "flex", gap: 2, justifyContent: "space-between" }}>
        <Box>
          <Typography
            component="h2"
            sx={{
              fontFamily: "fontFamily",
              fontSize: "h5.fontSize",
              fontWeight: "fontWeightBold",
              lineHeight: typographyTokens.lineHeight.heading,
            }}
          >
            Browse by skill
          </Typography>
          <Typography sx={{ color: "text.secondary", fontFamily: "fontFamily", fontSize: "body2.fontSize", mt: 0.5 }}>
            Choose a skill to see its patterns and game evidence below. Your game filters stay the same.
          </Typography>
        </Box>
        {selected && (
          <ButtonBase
            onClick={() => onSelect(null)}
            sx={{
              borderBottom: layoutTokens.dividerBorder,
              color: "primary.main",
              fontSize: "caption.fontSize",
              fontWeight: "fontWeightBold",
              letterSpacing: typographyTokens.tracking.label,
            }}
          >
            SHOW ALL PATTERNS
          </ButtonBase>
        )}
      </Box>
      <Box
        sx={{
          display: "grid",
          gap: 1.5,
          gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))", lg: "repeat(3, minmax(0, 1fr))" },
          mt: 1.75,
        }}
      >
        {areas.map((area) => {
          const active = selected === area.key;
          const volume = area.volume;
          const affectedGames = volume?.affectedGames ?? area.affectedGames;
          const maturity = volume?.maturity ?? area.maturity;
          return (
            <Box
              key={area.key}
              sx={{
                border: layoutTokens.dividerBorder,
                borderColor: active ? "primary.main" : "divider",
                borderLeft: active ? layoutTokens.accentBorder : layoutTokens.dividerBorder,
                bgcolor: active ? ACTIVE_SURFACE : "background.paper",
                color: active ? "background.default" : "text.primary",
                position: "relative",
              }}
            >
              <ButtonBase
                aria-label={`${maturityLabel[maturity]} ${area.label}`}
                aria-pressed={active}
                onClick={() => onSelect(active ? null : area.key)}
                sx={{
                  alignItems: "stretch",
                  borderRadius: 0,
                  display: "flex",
                  height: "100%",
                  justifyContent: "stretch",
                  textAlign: "left",
                  width: "100%",
                  "&:hover": { bgcolor: active ? surfaceTokens.inverseHover : "action.hover" },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flex: 1,
                    flexDirection: "column",
                    minHeight: layoutTokens.cardMinHeight,
                    p: { xs: 2, sm: 2.5 },
                    pr: 5,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      color: maturity === "established" ? "success.main" : "warning.dark",
                    }}
                  >
                    <DevelopmentGlyph area={area.key} />
                    <Typography
                      sx={{
                        fontSize: "caption.fontSize",
                        fontWeight: "fontWeightBold",
                        letterSpacing: typographyTokens.tracking.label,
                        textTransform: "uppercase",
                      }}
                    >
                      {maturityLabel[maturity]}
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      fontFamily: "fontFamily",
                      fontSize: "h6.fontSize",
                      fontWeight: "fontWeightBold",
                      letterSpacing: typographyTokens.tracking.heading,
                      lineHeight: typographyTokens.lineHeight.heading,
                      mt: 1,
                    }}
                  >
                    {area.label}
                  </Typography>
                  <Typography
                    sx={{
                      color: active ? ACTIVE_SURFACE_MUTED : "text.secondary",
                      fontFamily: "fontFamily",
                      fontSize: "body2.fontSize",
                      lineHeight: typographyTokens.lineHeight.body,
                      mt: 0.75,
                    }}
                  >
                    {area.description}
                  </Typography>
                  <Box
                    sx={{
                      borderTop: layoutTokens.dividerBorder,
                      borderColor: "divider",
                      display: "grid",
                      gridTemplateColumns: "repeat(2, 1fr)",
                      mt: "auto",
                      pt: 1,
                    }}
                  >
                    <Box>
                      <Typography
                        sx={{ fontFamily: "fontFamily", fontSize: "h6.fontSize", fontWeight: "fontWeightBold" }}
                      >
                        {affectedGames} of {volume?.analyzedGames ?? analyzedGames} games
                      </Typography>
                      <Typography
                        sx={{ color: active ? ACTIVE_SURFACE_MUTED : "text.secondary", fontSize: "caption.fontSize" }}
                      >
                        AFFECTED
                      </Typography>
                    </Box>
                    <Box>
                      <Typography
                        sx={{ fontFamily: "fontFamily", fontSize: "caption.fontSize", fontWeight: "fontWeightBold" }}
                      >
                        No area-level comparison available
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </ButtonBase>
              <IconButton
                aria-label={`About ${area.label}`}
                onClick={(event) => setInfo({ anchor: event.currentTarget, area })}
                size="small"
                sx={{
                  border: layoutTokens.dividerBorder,
                  borderColor: "divider",
                  borderRadius: 0,
                  color: active ? "background.default" : "text.secondary",
                  position: "absolute",
                  right: 10,
                  top: 10,
                }}
              >
                <InfoOutlinedIcon sx={{ fontSize: "body1.fontSize" }} />
              </IconButton>
            </Box>
          );
        })}
      </Box>
      <Popover
        open={info != null}
        anchorEl={info?.anchor}
        onClose={() => setInfo(null)}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        slotProps={{
          paper: {
            "aria-label": info ? `${info.area.label} information` : undefined,
            role: "dialog",
            sx: {
              border: layoutTokens.dividerBorder,
              borderColor: "divider",
              borderRadius: 0,
              boxShadow: 0,
              maxWidth: layoutTokens.compactMaxWidth,
              mt: 0.5,
              p: 2,
            },
          },
        }}
      >
        {info && (
          <Box>
            <MicroLabel>{info.area.label}</MicroLabel>
            <Typography
              sx={{
                fontSize: "body2.fontSize",
                fontWeight: "fontWeightBold",
                lineHeight: typographyTokens.lineHeight.body,
                mt: 0.75,
              }}
            >
              {areaHelp[info.area.key]?.meaning ?? info.area.description}
            </Typography>
            <Box sx={{ mt: 1.5 }}>
              <MicroLabel>What Nikola measures</MicroLabel>
            </Box>
            <Typography
              sx={{
                color: "text.secondary",
                fontSize: "caption.fontSize",
                lineHeight: typographyTokens.lineHeight.body,
                mt: 0.5,
              }}
            >
              {areaHelp[info.area.key]?.measures ?? info.area.description}
            </Typography>
            <Box sx={{ mt: 1.5 }}>
              <MicroLabel>What improvement looks like</MicroLabel>
            </Box>
            <Typography
              sx={{
                color: "text.secondary",
                fontSize: "caption.fontSize",
                lineHeight: typographyTokens.lineHeight.body,
                mt: 0.5,
              }}
            >
              {areaHelp[info.area.key]?.improvement ??
                "The pattern appears in fewer games and costs less when it occurs."}
            </Typography>
          </Box>
        )}
      </Popover>
    </Box>
  );
};
